import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { getEventById, getQuestionsByEvent } from "../lib/api";
import type { Event, Question } from "../lib/types";

export function useEvent(eventId: string | null) {
  const [event, setEvent] = useState<Event | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  // Per-instance token — see useClusters for the rationale.
  const instanceRef = useRef<string>(crypto.randomUUID());

  const load = useCallback(async () => {
    if (!eventId) { setLoading(false); return; }
    const e = await getEventById(eventId);
    if (!e) { setLoading(false); return; }
    setEvent(e);
    const qs = await getQuestionsByEvent(eventId);
    setQuestions(qs);
    setLoading(false);
  }, [eventId]);

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    load();

    const channel = supabase
      .channel(`event-${eventId}-${instanceRef.current}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "events", filter: `id=eq.${eventId}` }, (payload) => {
        setEvent(payload.new as Event);
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "questions" }, (payload) => {
        setQuestions((prev) =>
          prev.map((q) => (q.id === payload.new.id ? (payload.new as Question) : q))
        );
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "questions" }, (payload) => {
        const q = payload.new as Question;
        if (q.event_id === eventId) {
          setQuestions((prev) => {
            // The row can arrive twice: once via an explicit reload() after
            // createQuestion, once via this realtime event — keep it idempotent.
            if (prev.some((x) => x.id === q.id)) return prev;
            return [...prev, q].sort((a, b) => a.order_index - b.order_index);
          });
        }
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "questions" }, (payload) => {
        setQuestions((prev) => prev.filter((q) => q.id !== payload.old.id));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [eventId, load]);

  const activeQuestion = questions.find((q) => q.is_open) ?? null;

  return { event, questions, activeQuestion, loading, reload: load };
}
